import tokenStorageService from "../../../services/TokenStorageService";
import gatewayService from '../../../services/GatewayService';
import {editUserData, createUserData} from "../../../services/ConverterService";
import * as CONSTANTS from '../../constants';
import {eventTracker} from "../../../services/GoogleAnalyticsService";

/**
 * Get all non admin Users for a specific supplier.
 *
 * @param commit
 * @param payload
 * @returns {Promise.<TResult>}
 */
export const getUsers = ({ commit }, payload) => {
    const token = tokenStorageService.get();
    commit(CONSTANTS.ADMIN_USERS_GET_PENDING);
    return gatewayService.getUsers(payload, token).then((res) => {
        if (res.status === 200) {
            commit(CONSTANTS.ADMIN_USERS_GET_SUCCESS, res.data.data);
        } else {
            commit(CONSTANTS.ADMIN_USERS_GET_NOCONTENT);
        }
    }).catch(() => {
        commit(CONSTANTS.ADMIN_USERS_GET_FAILURE);
    });
};

/**
 * Get a specific user.
 *
 * @param commit
 * @param payload, tje id of the User.
 * @returns {Promise.<T>}
 */
export const getUser = ({ commit }, payload) => {
    const token = tokenStorageService.get();
    commit(CONSTANTS.ADMIN_USER_GET_PENDING);
    return gatewayService.getUser(payload, token).then((res) => {
        if (res.status === 200) {
            commit(CONSTANTS.ADMIN_USER_GET_SUCCESS, res.data.data);
        } else {
            commit(CONSTANTS.ADMIN_USER_GET_NOCONTENT);
        }
    }).catch(() => {
        commit(CONSTANTS.ADMIN_USER_GET_FAILURE)
    });
};

/**
 * Submit the form and display the accept modal.
 *
 * @param commit
 */
export const submitCreateUserForm = ({ commit }) => {
    commit(CONSTANTS.ADMIN_FORM_CREATE_SUBMIT);
};

/**
 * Cancel the form and close the accept modal.
 *
 * @param commit
 */
export const cancelCreateUserForm = ({ commit }) => {
    commit(CONSTANTS.ADMIN_FORM_CREATE_CANCEL);
};

/**
 * Accept the form.
 *
 * @param commit
 */
export const acceptCreateUserForm = ({ commit }) => {
    commit(CONSTANTS.ADMIN_FORM_CREATE_ACCEPT);
};

/**
 * Submit the form and display accept modal.
 *
 * @param commit
 */
export const submitEditUserForm = ({ commit }) => {
    commit(CONSTANTS.ADMIN_FORM_EDIT_SUBMIT);
};

/**
 * Cancel the form and close the accept modal.
 *
 * @param commit
 */
export const cancelEditUserForm = ({ commit }) => {
    commit(CONSTANTS.ADMIN_FORM_EDIT_CANCEL);
};

/**
 * Accept the form.
 *
 * @param commit
 */
export const acceptEditUserForm = ({ commit }) => {
    commit(CONSTANTS.ADMIN_FORM_EDIT_ACCEPT);
};

/**
 * Create a user.
 *
 * @param commit
 * @param dispatch
 * @param rootState
 * @param payload
 * @returns {Promise<T>}
 */
export const createUser = ({ commit, dispatch, rootState }, payload) => {
    const token = tokenStorageService.get();
    const user = rootState.auth.user;
    const supplierId = rootState.auth.supplier.id;
    commit(CONSTANTS.ADMIN_USER_CREATE_PENDING);
    return gatewayService.createUser(supplierId, createUserData(payload), token).then(() => {
        commit(CONSTANTS.ADMIN_USER_CREATE_SUCCESS, {
            // Google Analytics
            meta: eventTracker('admin', 'user', 'user created', user.id)
        });
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'created',
            level: 'success'
        }, {root: true});
        dispatch('toggleCreateModal', false);
        dispatch('getUsers', supplierId);
    }).catch((err) => {
        dispatch('toggleCreateModal', false);
        if(err.status === 400) {
            commit(CONSTANTS.ADMIN_USER_CREATE_FAILURE);
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'email',
                level: 'danger'
            }, {root: true});
        } else {
            commit(CONSTANTS.ADMIN_USER_CREATE_FAILURE);
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'error',
                level: 'danger'
            }, {root: true});
        }
    })
};

/**
 * Update a specific User.
 *
 * @param commit
 * @param rootState
 * @param dispatch
 * @param payload, the id of the User.
 * @returns {Promise.<TResult>}
 */
export const editUser = ({ commit, rootState, dispatch }, payload) => {
    // If new roles are added then call the update roles
    const token = tokenStorageService.get();
    const user = rootState.auth.user;
    const supplierId = rootState.auth.supplier.id;
    commit(CONSTANTS.ADMIN_USER_EDIT_PENDING);
    return gatewayService.editUser(supplierId, editUserData(payload), token).then(() => {
        commit(CONSTANTS.ADMIN_USER_EDIT_SUCCESS, {
            // Google Analytics
            meta: eventTracker('admin', 'user', 'user updated', user.id)
        });
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'updated',
            level: 'success'
        }, {root: true});
        dispatch('toggleEditModal', false);
        dispatch('getUsers', supplierId);
    }).catch(() => {
        commit(CONSTANTS.ADMIN_USER_EDIT_FAILURE);
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'error',
            level: 'danger'
        }, {root: true});
        dispatch('toggleEditModal', false);
    });
};

/**
 * Delete a specific User.
 *
 * @param commit
 * @param rootState
 * @param dispatch
 * @param payload, the id of the User.
 * @returns {Promise.<TResult>}
 */
export const deleteUser = ({ commit, rootState, dispatch }, payload) => {
    const token = tokenStorageService.get();
    const user = rootState.auth.user;
    const supplierId = rootState.auth.supplier.id;
    commit(CONSTANTS.ADMIN_USER_EDIT_PENDING);
    return gatewayService.deleteUser(payload, token).then(() => {
        commit(CONSTANTS.ADMIN_USER_EDIT_SUCCESS, {
            // Google Analytics
            meta: eventTracker('admin', 'user', 'user deleted', user.id)
        });
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'deleted',
            level: 'success'
        }, {root: true});
        dispatch('toggleEditModal', false);
        dispatch('getUsers', supplierId);
    }).catch(() => {
        commit(CONSTANTS.ADMIN_USER_EDIT_FAILURE);
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'error',
            level: 'danger'
        }, {root: true});
        dispatch('toggleEditModal', false);
    })
};

/**
 * Block a specific User.
 *
 * @param commit
 * @param rootState
 * @param dispatch
 * @param payload, the id of the User.
 * @returns {Promise.<TResult>}
 */
export const blockUser = ({ commit, rootState, dispatch }, payload) => {
    const token = tokenStorageService.get();
    const user = rootState.auth.user;
    const supplierId = rootState.auth.supplier.id;
    commit(CONSTANTS.ADMIN_USER_EDIT_PENDING);
    return gatewayService.blockUser(payload, token).then(() => {
        commit(CONSTANTS.ADMIN_USER_EDIT_SUCCESS, {
            // Google Analytics
            meta: eventTracker('admin', 'user', 'user blocked', user.id)
        });
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'blocked',
            level: 'success'
        }, {root: true});
        dispatch('toggleEditModal', false);
        dispatch('getUsers', supplierId);
    }).catch(() => {
        commit(CONSTANTS.ADMIN_USER_EDIT_FAILURE);
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'error',
            level: 'danger'
        }, {root: true});
        dispatch('toggleEditModal', false);
    });
};

/**
 * Unblock a specific User.
 *
 * @param commit
 * @param rootState
 * @param dispatch
 * @param payload, the id of the User.
 * @returns {Promise.<TResult>}
 */
export const unblockUser = ({ commit, rootState, dispatch }, payload) => {
    const token = tokenStorageService.get();
    const user = rootState.auth.user;
    const supplierId = rootState.auth.supplier.id;
    commit(CONSTANTS.ADMIN_USER_EDIT_PENDING);
    return gatewayService.unblockUser(payload, token).then(() => {
        commit(CONSTANTS.ADMIN_USER_EDIT_SUCCESS, {
            // Google Analytics
            meta: eventTracker('admin', 'user', 'user unblocked', user.id)
        });
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'unblocked',
            level: 'success'
        }, {root: true});
        dispatch('toggleEditModal', false);
        dispatch('getUsers', supplierId);
    }).catch(() => {
        commit(CONSTANTS.ADMIN_USER_EDIT_FAILURE);
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'error',
            level: 'danger'
        }, {root: true});
        dispatch('toggleEditModal', false);
    });
};

/**
 * Sets the user to edit in state.
 *
 * @param commit
 * @param payload
 */
export const setUser = ({ commit }, payload) => {
    commit(CONSTANTS.ADMIN_USER_SET, payload);
};

/**
 * Unsets the user to edit in state.
 *
 * @param commit
 */
export const unsetUser = ({ commit }) => {
    commit(CONSTANTS.ADMIN_USER_UNSET);
};

/**
 * Update the user values in the edit modal.
 *
 * @param commit
 * @param payload
 */
export const updateEditUserValues = ({ commit }, payload) => {
    commit(CONSTANTS.ADMIN_USER_EDIT_VALUES, payload)
};

/**
 * Update the user values for the create modal.
 *
 * @param commit
 * @param payload
 */
export const updateCreateUserValues = ({ commit }, payload) => {
    commit(CONSTANTS.ADMIN_USER_CREATE_VALUES, payload);
};

/**
 * Toggle the edit user modal, send false as payload to force close.
 *
 * @param commit
 * @param payload
 */
export const toggleEditModal = ({commit}, payload) => {
    commit(CONSTANTS.ADMIN_USER_EDIT_MODAL_TOGGLE, payload);
};

/**
 * Toggle the edit user modal, send false as payload to force close.
 *
 * @param commit
 * @param payload
 */
export const toggleCreateModal = ({commit}, payload) => {
    commit(CONSTANTS.ADMIN_USER_CREATE_MODAL_TOGGLE, payload);
};

/**
 * Fetch the roles for the supplier.
 *
 * @param commit
 * @param payload
 * @returns {Promise<T>}
 */
export const getRoles = ({ commit }, payload) => {
    const token = tokenStorageService.get();
    commit(CONSTANTS.ADMIN_ROLES_GET_PENDING);
    return gatewayService.getRoles(payload, token).then((res) => {
        if(res.status === 200) {
            commit(CONSTANTS.ADMIN_ROLES_GET_SUCCESS, res.data.data);
        } else {
            commit(CONSTANTS.ADMIN_ROLES_GET_NO_CONTENT)
        }
    }).catch(() => {
        commit(CONSTANTS.ADMIN_ROLES_GET_FAILURE);
    })
};

/**
 * Fetch the supported languages.
 *
 * @param commit
 * @returns {Promise<T>}
 */
export const getLanguages = ({ commit }) => {
    const token = tokenStorageService.get();
    commit(CONSTANTS.ADMIN_LANGUAGES_GET_PENDING);
    return gatewayService.getLanguages(token).then((res) => {
        if(res.status === 200) {
            commit(CONSTANTS.ADMIN_LANGUAGES_GET_SUCCESS, res.data.data);
        } else {
            commit(CONSTANTS.ADMIN_LANGUAGES_GET_NO_CONTENT)
        }
    }).catch(() => {
        commit(CONSTANTS.ADMIN_LANGUAGES_GET_FAILURE);
    })
};



// WEBPACK FOOTER //
// ./src/store/modules/admin/actions.js